import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import * as FullStory from '@fullstory/browser';
import { getSessionStorage } from '@whitelabel/helpers/storageUtils';
import { IHelpPagePartner } from '../../helpers/types';
import { ZENDESK_CHAT_APP_ID, ZENDESK_CHAT_PARTNER } from './helper';
import { ZendeskChatContext } from './context';

const ZendeskChatProvider = ({ children }: { children: ReactNode }) => {
  const [isZendeskChatOpen, setIsZendeskChatOpen] = useState<boolean>(false);
  const [isZendeskChatEnabled, setIsZendeskChatEnabled] = useState<boolean>(false);
  const [zendeskChatPartners, setZendeskChatPartners] = useState<IHelpPagePartner[]>();

  const launchZendeskChat = useCallback(
    (partnerSlug: string) =>
      new Promise<void>((resolve) => {
        const zendeskChatAPPID = ZENDESK_CHAT_APP_ID[partnerSlug];
        if (!zendeskChatAPPID) return;
        const isScriptExist = document.getElementById('ze-snippet')?.getAttribute('src')?.includes(zendeskChatAPPID);
        if (isScriptExist) return;
        const scriptURL = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskChatAPPID}`;
        const script = document.createElement('script');
        script.src = scriptURL;
        script.async = true;
        script.id = 'ze-snippet';
        script.onload = () => {
          setIsZendeskChatOpen(true);
          sessionStorage.setItem(ZENDESK_CHAT_PARTNER, partnerSlug);
          resolve();
        };
        document.body.appendChild(script);
      }),
    [],
  );

  const openChat = useCallback(
    async (partnerSlug: string) => {
      if (isZendeskChatOpen || !isZendeskChatEnabled) return;

      await launchZendeskChat(partnerSlug);
      window.zE?.('messenger', 'show');
      window.zE?.('messenger', 'open');
      if (!window.isHeadless && window.bwtag) {
        window.bwtag('record', 'contact-us', {
          partnerSlug,
          openChat: true,
        });
        FullStory.event('ZendeskChat', {
          partnerSlug,
          openChat: true,
        });
      }
    },
    [launchZendeskChat, isZendeskChatEnabled, isZendeskChatOpen],
  );

  useEffect(() => {
    // load Zendesk Chat if partner slug is in session storage
    const zendeskChatPartnerFromSession = getSessionStorage(ZENDESK_CHAT_PARTNER);
    if (zendeskChatPartnerFromSession) {
      void launchZendeskChat(zendeskChatPartnerFromSession);
      setIsZendeskChatEnabled(true);
    }
  }, [launchZendeskChat]);

  useEffect(() => {
    // check if Zendesk Chat is enabled
    if (zendeskChatPartners) {
      const isSinglePartner = zendeskChatPartners.length === 1;
      const hasZendeskChat = !!ZENDESK_CHAT_APP_ID[zendeskChatPartners[0].slug];
      setIsZendeskChatEnabled(isSinglePartner && hasZendeskChat);
    }
  }, [zendeskChatPartners]);

  const contextValue = useMemo(
    () => ({ openChat, isZendeskChatOpen, setZendeskChatPartners, isZendeskChatEnabled }),
    [isZendeskChatOpen, openChat, setZendeskChatPartners, isZendeskChatEnabled],
  );

  return <ZendeskChatContext.Provider value={contextValue}>{children}</ZendeskChatContext.Provider>;
};

export default ZendeskChatProvider;
